/**
 * What we know about a manufacturer's hardware, shown beside a base's codes.
 *
 * Support knowledge that used to live in somebody's head and be re-asked on
 * every installation: where the base shows its own IP, what the factory PIN is,
 * what the web interface ships with, the one setting that is always wrong out
 * of the box. It sits next to the fields it is about because that is where the
 * question is asked — nobody goes looking for a wiki while a client waits.
 *
 * Two deliberate choices:
 *
 *  - the **labels** are translated, the **values** are not. `Status > Base` and
 *    `March last sunday` are what is written on the phone's own screen, in the
 *    phone's own English, and translating them would send someone hunting for a
 *    menu that does not exist.
 *  - the default credentials here are *factory* values, not this client's. A
 *    base still on `admin:admin` is a finding, which is exactly why it is worth
 *    showing next to the field that says what it actually is.
 */

export type BrandDocItem = {
  /** Label key under `devices.bases.brandDocs.items` in the locales. */
  key: "webAccess" | "defaultPin" | "defaultLogin" | "dst";
  /** Printed as-is, monospaced: device-side literals, not prose. */
  value: string;
};

export type BrandDoc = {
  /** Shown as the panel's title — the vendor, not the model. */
  vendor: string;
  /**
   * Matched against the free-text `brand` field ("Yealink W73P"), which is what
   * somebody typed off the box. Case-insensitive and substring-based on
   * purpose: the model varies, the vendor does not.
   */
  match: RegExp;
  items: BrandDocItem[];
};

const BRAND_DOCS: BrandDoc[] = [
  {
    vendor: "Yealink",
    match: /yealink/i,
    items: [
      { key: "webAccess", value: "Bouton principal → Status > Base" },
      { key: "defaultPin", value: "0000" },
      { key: "defaultLogin", value: "admin:admin" },
      {
        key: "dst",
        value: "Enabled — March last sunday → October last sunday, 60 minutes",
      },
    ],
  },
];

/**
 * The documentation for a typed brand, or nothing.
 *
 * Nothing is the honest answer for a vendor we have not written up: an empty
 * panel would read as "this base has no defaults", which is the opposite of
 * what it means.
 */
export const brandDocFor = (brand: string): BrandDoc | undefined =>
  brand ? BRAND_DOCS.find((doc) => doc.match.test(brand)) : undefined;

/** Every vendor we have notes for, for the brand field's suggestions. */
export const documentedVendors = () => BRAND_DOCS.map((doc) => doc.vendor);
